import { useState, useEffect } from "react";
import { X, ChevronDown } from "lucide-react";

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [consents, setConsents] = useState({
    analytics: false,
    research: false,
    marketing: false,
    functional: true,
  });

  useEffect(() => {
    const storedConsent = localStorage.getItem("cookieConsent");
    if (!storedConsent) {
      setIsVisible(true);
    } else {
      const parsed = JSON.parse(storedConsent);
      setConsents(parsed);
    }
  }, []);

  const handleConsent = (type: keyof typeof consents, value: boolean) => {
    setConsents((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const saveCookiePreferences = () => {
    localStorage.setItem("cookieConsent", JSON.stringify(consents));
    setIsVisible(false);
  };

  const acceptAll = () => {
    const allConsents = {
      analytics: true,
      research: true,
      marketing: true,
      functional: true,
    };
    setConsents(allConsents);
    localStorage.setItem("cookieConsent", JSON.stringify(allConsents));
    setIsVisible(false);
  };

  const rejectAll = () => {
    const minimalConsents = {
      analytics: false,
      research: false,
      marketing: false,
      functional: true,
    };
    setConsents(minimalConsents);
    localStorage.setItem("cookieConsent", JSON.stringify(minimalConsents));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Banner */}
      <div className="bg-brand-martinique border-t border-white/20 shadow-2xl">
        <div className="max-w-[1440px] mx-auto px-8 py-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="font-agency text-h5 text-white mb-2">
                Privacy & Cookie Settings
              </h3>
              <p className="font-martianmono text-text-sm-custom text-brand-gray">
                We are committed to transparency. This research helps us improve
                government accountability tools and understand barriers to
                public access.
              </p>
            </div>
            <button
              onClick={() => {
                setIsVisible(false);
              }}
              className="flex-shrink-0 p-2 hover:bg-white/10 rounded transition-colors text-white"
              aria-label="Close cookie banner"
            >
              <X size={20} />
            </button>
          </div>

          {/* Expandable Details */}
          {!isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              className="flex items-center gap-2 text-brand-orange hover:text-orange-400 transition-colors mb-4 font-martianmono text-text-sm-custom"
            >
              <span>View detailed settings</span>
              <ChevronDown size={16} />
            </button>
          )}

          {isExpanded && (
            <div className="space-y-4 mb-6 pb-6 border-b border-white/10">
              {/* Analytics Consent */}
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consents.analytics}
                    onChange={(e) =>
                      handleConsent("analytics", e.target.checked)
                    }
                    className="mt-1 w-4 h-4 rounded border-white/30 bg-brand-martinique cursor-pointer"
                  />
                  <div>
                    <p className="font-martianmono text-text-md-custom text-white font-semibold">
                      Analytics
                    </p>
                    <p className="font-martianmono text-text-sm-custom text-brand-gray">
                      Track page views, state selections, and exemption reviews
                      to understand user behavior patterns.
                    </p>
                  </div>
                </label>
              </div>

              {/* Research Consent */}
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consents.research}
                    onChange={(e) =>
                      handleConsent("research", e.target.checked)
                    }
                    className="mt-1 w-4 h-4 rounded border-white/30 bg-brand-martinique cursor-pointer"
                  />
                  <div>
                    <p className="font-martianmono text-text-md-custom text-white font-semibold">
                      Research
                    </p>
                    <p className="font-martianmono text-text-sm-custom text-brand-gray">
                      Use your FOIA requests and AI feedback to improve our
                      tools and train our agent. Data is anonymized for research
                      purposes.
                    </p>
                  </div>
                </label>
              </div>

              {/* Marketing Consent */}
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consents.marketing}
                    onChange={(e) =>
                      handleConsent("marketing", e.target.checked)
                    }
                    className="mt-1 w-4 h-4 rounded border-white/30 bg-brand-martinique cursor-pointer"
                  />
                  <div>
                    <p className="font-martianmono text-text-md-custom text-white font-semibold">
                      Marketing & Communications
                    </p>
                    <p className="font-martianmono text-text-sm-custom text-brand-gray">
                      Send you updates about our research, new features, and
                      foundation initiatives.
                    </p>
                  </div>
                </label>
              </div>

              {/* Functional Consent */}
              <div className="space-y-2">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="mt-1 w-4 h-4 rounded border-white/30 bg-brand-martinique cursor-not-allowed"
                  />
                  <div>
                    <p className="font-martianmono text-text-md-custom text-white font-semibold">
                      Functional (Required)
                    </p>
                    <p className="font-martianmono text-text-sm-custom text-brand-gray">
                      Essential features that keep our platform running. These
                      cannot be disabled.
                    </p>
                  </div>
                </label>
              </div>

              {/* Learn More Link */}
              <div className="pt-2">
                <a
                  href="/privacy"
                  className="font-martianmono text-text-sm-custom text-brand-orange hover:text-orange-400 transition-colors"
                >
                  Read our full Privacy Policy &amp; Terms of Service
                </a>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={rejectAll}
              className="flex-1 px-4 py-3 border border-white/30 text-white font-agency text-xl rounded hover:bg-white/10 transition-colors"
            >
              Reject Non-Essential
            </button>
            <button
              onClick={saveCookiePreferences}
              className="flex-1 px-4 py-3 bg-white/20 text-white font-agency text-xl rounded hover:bg-white/30 transition-colors"
            >
              Save Preferences
            </button>
            <button
              onClick={acceptAll}
              className="flex-1 px-4 py-3 bg-brand-orange text-white font-agency text-xl rounded hover:bg-orange-600 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
